import * as React from "react";
import { uniqBy, flatten, sortBy } from "lodash";
import { withStateHandlers } from "recompose";
import { graphql, compose } from "react-apollo";
import { Flex, Box } from "grid-styled";
import { gql } from "apollo-boost";
import { Helmet } from "react-helmet";
import InputContainer from "./forms/InputContainer";
import Alert from "./Alert";
import Select from "./forms/Select";
import FranchiseSelect from "./forms/FranchiseSelect";
import JobsListPlaceholder from "./JobsListPlaceholder";
import JobsList from "./JobsList";
import { LinkButton } from "./Button";
import joinPath from "../utils/join-path";
import { getPreferences, setPreferences } from "../utils/localStorage";
import { SERVICE_TYPES, STATUSES } from "../constants";
import InfiniteScroller from "./InfiniteScroller";

const ITEMS_PER_PAGE = 15;

const UNIQ_STATUSES = sortBy(
  uniqBy(flatten(Object.values(STATUSES)), "value"),
  "position"
);

const Jobs = props => {
  const { match, loading, error, jobs, hasMore, loadMoreEntries } = props;

  const renderJobsList = () => {
    if (loading && !jobs) return <JobsListPlaceholder />;
    if (error) return <div>Error</div>;
    return <JobsList jobs={jobs} match={match} />;
  };

  return (
    <Box p={3}>
      <Helmet>
        <title>Jobs</title>
      </Helmet>
      {props.singleFranchise && (
        <Box mb={2}>
          <Alert variant="info">
            Jobs submitted after 10am ET will apply to next business day's turn
            around.
          </Alert>
        </Box>
      )}
      <Flex pb={2} mx={-2} wrap="wrap">
        {!props.singleFranchise && (
          <Box w={1 / 4} px={2}>
            <InputContainer
              name="franchise"
              labelText="Franchise"
              placeholder="Filter by franchise"
              value={props.franchiseId}
              onChange={props.setFranchiseId}
              fieldComponent={FranchiseSelect}
            />
          </Box>
        )}
        <Box w={1 / 4} px={2}>
          <InputContainer
            name="serviceType"
            labelText="Service"
            placeholder="Filter by service type"
            value={props.serviceType}
            onChange={props.setServiceType}
            fieldComponent={Select}
            options={SERVICE_TYPES}
            labelKey="name"
            valueKey="name"
          />
        </Box>
        <Box w={1 / 4} px={2}>
          <InputContainer
            name="serviceStatus"
            labelText="Status"
            placeholder="Filter by service status"
            value={props.serviceStatus}
            onChange={props.setServiceStatus}
            fieldComponent={Select}
            options={UNIQ_STATUSES}
          />
        </Box>
        <Flex px={2} align="flex-end" style={{ marginLeft: "auto" }}>
          <LinkButton variant="primary" to={joinPath(match.url, `/new`)}>
            Add a job
          </LinkButton>
        </Flex>
      </Flex>
      <InfiniteScroller hasMore={hasMore} loadMore={loadMoreEntries}>
        {renderJobsList()}
      </InfiniteScroller>
    </Box>
  );
};

export const JobsQuery = gql`
  query JobsQuery(
    $offset: Int
    $franchiseId: ID
    $serviceType: String
    $serviceStatus: String
    $showArchived: Boolean
  ) {
    jobs(
      offset: $offset
      franchiseId: $franchiseId
      serviceType: $serviceType
      serviceStatus: $serviceStatus
      showArchived: $showArchived
    ) {
      ...JobsListJob
    }
  }
  ${JobsList.fragments.job}
`;

const preferenceKey = "jobsFilters";

const setAndSaveState = key => value => {
  const updatedState = { [key]: value };
  setPreferences(preferenceKey, updatedState);
  return updatedState;
};

const enhance = compose(
  withStateHandlers(
    ({ franchiseId }) => {
      const {
        franchiseId: preferredFranchiseId,
        serviceType,
        serviceStatus
      } = getPreferences(preferenceKey);
      return {
        singleFranchise: !!franchiseId,
        franchiseId: franchiseId || preferredFranchiseId || null,
        serviceType: serviceType || "",
        serviceStatus: serviceStatus || ""
      };
    },
    {
      setFranchiseId: () => setAndSaveState("franchiseId"),
      setServiceType: () => setAndSaveState("serviceType"),
      setServiceStatus: () => setAndSaveState("serviceStatus")
    }
  ),
  graphql(JobsQuery, {
    options: props => ({
      variables: {
        franchiseId: props.franchiseId || "",
        serviceType: props.serviceType || "",
        serviceStatus: props.serviceStatus || "",
        showArchived: false,
        offset: 0,
        limit: ITEMS_PER_PAGE
      },
      fetchPolicy: "network-only"
    }),
    props({ data: { loading, error, jobs, fetchMore } }) {
      return {
        loading,
        error,
        jobs,
        hasMore: jobs && jobs.length >= ITEMS_PER_PAGE,
        loadMoreEntries() {
          return fetchMore({
            variables: {
              offset: jobs.length
            },
            updateQuery: (previousResult, { fetchMoreResult }) => {
              if (!fetchMoreResult) {
                return previousResult;
              }

              return {
                ...previousResult,
                jobs: [...previousResult.jobs, ...fetchMoreResult.jobs]
              };
            }
          });
        }
      };
    }
  })
);

export default enhance(Jobs);



// WEBPACK FOOTER //
// ./src/components/Jobs.js